import { action, makeObservable, observable } from "mobx";
import { Subscription, finalize } from "rxjs";
import { IRootStore } from "src/Shared/Stores/Root.store";
import SettingsService from "../Services/Settings.service";
import { ILeadTypeSetting } from "../Interfaces/ILeadTypeSetting";
import { AxiosError } from "axios";
import { ToastService } from "src/Shared/Services";
import { IUser, IPageRequest, IPagedResult } from "src/Shared/Interfaces";
import AuthService from "src/Shared/Services/Auth.service";

export class SettingsStore {
  rootStore: IRootStore;

  public gridLoading: boolean = false;
  public leadTypeList: ILeadTypeSetting[] = [];
  public userList: IPagedResult<IUser> = {
    pageNumber: 1,
    pageSize: 10,
    results: [],
    totalNumberOfRecords: 0,
  };

  constructor(rootStore: IRootStore) {
    makeObservable(this, {
      gridLoading: observable,
      leadTypeList: observable,
      userList: observable,

      setGridLoading: action,
      setLeadTypeList: action,
      setUserList: action,

      getLeadTypes: action.bound,
      setLeadTypeStatus: action.bound,
      getUsers: action.bound,
      impersonateUser: action.bound,
    });

    this.rootStore = rootStore;
  }

  setGridLoading = (flag: boolean): void => {
    this.gridLoading = flag;
  };

  getLeadTypes(): Subscription {
    this.setGridLoading(true);

    return SettingsService.getLeadTypes()
      .pipe(
        finalize(() => {
          this.setGridLoading(false);
        }),
      )
      .subscribe({
        next: (data) => {
          this.setLeadTypeList(data);
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  }

  setLeadTypeList(data: ILeadTypeSetting[]) {
    if (data) {
      this.leadTypeList = data;
    }
  }

  setUserList(data: IPagedResult<IUser>) {
    if (data) {
      this.userList = data;
    }
  }

  getUsers(request: IPageRequest<any>): Subscription {
    this.setGridLoading(true);
    return SettingsService.getUsers(request)
      .pipe(
        finalize(() => {
          this.setGridLoading(false);
        }),
      )
      .subscribe({
        next: (data) => {
          this.setUserList(data);
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  }

  setLeadTypeStatus(id: number, isActive: boolean): Subscription {
    this.setGridLoading(true);
    return SettingsService.setLeadTypeStatus(id, isActive)
      .pipe(
        finalize(() => {
          this.setGridLoading(false);
        }),
      )
      .subscribe({
        next: (res) => {
          ToastService.success(res);
          this.getLeadTypes();
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  }

  impersonateUser(userId: number): Subscription {
    this.setGridLoading(true);
    return AuthService.impersonateUser(userId)
      .pipe(
        finalize(() => {
          this.setGridLoading(false);
        }),
      )
      .subscribe({
        next: (success: any) => {
          if (success && success.token) {
            const existingAuthorizationToken = localStorage.getItem("AuthToken");
            if (existingAuthorizationToken != null) {
              localStorage.setItem("Abp.ImperonatingToken", existingAuthorizationToken);
              localStorage.setItem("Abp.ImperonatingUrl", window.location.pathname + window.location.search);
              localStorage.setItem("AuthToken", success.token);
              window.location.href = "/company";
            }
          }
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  }
}
